import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { useUnit } from "effector-react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { ReactComponent as AnalyticsIcon } from "../assets/icons/sidebar/analytics.svg";
import { ReactComponent as CalendarIcon } from "../assets/icons/sidebar/calendar.svg";
import { ReactComponent as HistoryIcon } from "../assets/icons/sidebar/history.svg";
import { ReactComponent as ImportIcon } from "../assets/icons/sidebar/import.svg";
import { ReactComponent as KeyIcon } from "../assets/icons/sidebar/key.svg";
import { ReactComponent as LoRaWanIcon } from "../assets/icons/sidebar/lorawan.svg";
import { ReactComponent as MegaphoneIcon } from "../assets/icons/sidebar/megaphone.svg";
import { ReactComponent as MobileUsersIcon } from "../assets/icons/sidebar/mobile-users.svg";
import { ReactComponent as AgentIcon } from "../assets/icons/sidebar/agent.svg";
import { ReactComponent as PhoneIcon } from "../assets/icons/sidebar/phone.svg";
import { ReactComponent as PlusIcon } from "../assets/icons/sidebar/plus.svg";
import { ReactComponent as RocketIcon } from "../assets/icons/sidebar/rocket.svg";
import { ReactComponent as SettingsIcon } from "../assets/icons/sidebar/settings.svg";
import { ReactComponent as SunIcon } from "../assets/icons/sidebar/sun.svg";
import { ReactComponent as TicketsIcon } from "../assets/icons/sidebar/tickets.svg";
import { ReactComponent as UserIcon } from "../assets/icons/sidebar/user.svg";
import { $canEditComplex, $isAuthenticated, $isSettlementTenant, $user } from "../store/auth";
import { userHasRole } from "../config/roles";
import { useAppTheme } from "../theme/AppThemeProvider";
import { $activeTenantId } from "../store/tenantContext";
import { fetchResidentialComplexesFx } from "../store/complexes";
import { getObjectKindLabels } from "../utils/complexKind";
import { ticketsApi } from "../api/tickets";
import { gatewaysApi } from "../api/gateways";

import logo from "../assets/images/logo.svg";
import "./Sidebar.css";

const SIDEBAR_EXPANDED_WIDTH = "220px";
const SIDEBAR_COLLAPSED_WIDTH = "56px";
const PEEK_OPEN_DELAY_MS = 200;
const PEEK_CLOSE_DELAY_MS = 280;

const Sidebar = () => {
  const isAuthenticated = useUnit($isAuthenticated);
  const user = useUnit($user);
  const canEditComplex = useUnit($canEditComplex);
  const isSettlementTenant = useUnit($isSettlementTenant);
  const activeTenantId = useUnit($activeTenantId);
  const location = useLocation();
  const { themeId, setThemeId } = useAppTheme();
  const navRef = useRef(null);
  const peekOpenTimerRef = useRef(null);
  const peekCloseTimerRef = useRef(null);
  const labels = useMemo(
    () => getObjectKindLabels(isSettlementTenant),
    [isSettlementTenant]
  );

  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = window.localStorage.getItem("sidebar-collapsed");
      return saved === null ? true : saved === "1";
    } catch (error) {
      return true;
    }
  });
  const [isPeeking, setIsPeeking] = useState(false);
  const [canScrollNav, setCanScrollNav] = useState(false);
  const [navScrollEdges, setNavScrollEdges] = useState({ top: false, bottom: false });
  const [newTicketsCount, setNewTicketsCount] = useState(0);
  const [gatewayProblemsCount, setGatewayProblemsCount] = useState(0);

  const clearPeekTimers = useCallback(() => {
    if (peekOpenTimerRef.current) {
      window.clearTimeout(peekOpenTimerRef.current);
      peekOpenTimerRef.current = null;
    }
    if (peekCloseTimerRef.current) {
      window.clearTimeout(peekCloseTimerRef.current);
      peekCloseTimerRef.current = null;
    }
  }, []);

  const updateNavScrollState = useCallback(() => {
    const node = navRef.current;
    if (!node) return;
    const maxScroll = node.scrollHeight - node.clientHeight;
    const canScroll = maxScroll > 2;
    setCanScrollNav(canScroll);
    setNavScrollEdges({
      top: canScroll && node.scrollTop > 2,
      bottom: canScroll && node.scrollTop < maxScroll - 2,
    });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (
      (userHasRole(user, "contractor") || userHasRole(user, "superadmin")) &&
      !activeTenantId
    ) {
      return;
    }
    fetchResidentialComplexesFx();
  }, [isAuthenticated, activeTenantId, user]);

  useEffect(() => {
    try {
      window.localStorage.setItem("sidebar-collapsed", isCollapsed ? "1" : "0");
    } catch (error) {
      // ignore storage errors
    }
  }, [isCollapsed]);

  useEffect(() => {
    const width = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;
    document.documentElement.style.setProperty("--sidebar-current-width", width);
    document.documentElement.style.setProperty("--sidebar-rail-width", width);
    window.dispatchEvent(
      new CustomEvent("sidebar-state-changed", {
        detail: {
          collapsed: isCollapsed,
          width,
        },
      })
    );
  }, [isCollapsed]);

  useEffect(() => {
    if (!isCollapsed) {
      clearPeekTimers();
      setIsPeeking(false);
    }
  }, [isCollapsed, clearPeekTimers]);

  useEffect(() => {
    const peeking = Boolean(isCollapsed && isPeeking);
    document.documentElement.dataset.sidebarPeek = peeking ? "1" : "0";
    window.dispatchEvent(
      new CustomEvent("sidebar-peek-changed", {
        detail: { peeking },
      })
    );
    return () => {
      document.documentElement.dataset.sidebarPeek = "0";
    };
  }, [isCollapsed, isPeeking]);

  useEffect(() => {
    updateNavScrollState();
  }, [updateNavScrollState, isCollapsed, isPeeking, location.pathname]);

  useEffect(() => {
    const node = navRef.current;
    if (!node) return undefined;

    updateNavScrollState();
    node.addEventListener("scroll", updateNavScrollState, { passive: true });

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => updateNavScrollState());
      resizeObserver.observe(node);
    }

    window.addEventListener("resize", updateNavScrollState);
    return () => {
      node.removeEventListener("scroll", updateNavScrollState);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateNavScrollState);
    };
  }, [updateNavScrollState]);

  useEffect(() => () => clearPeekTimers(), [clearPeekTimers]);

  const canManageComplexes = canEditComplex;
  const canManageImports =
    userHasRole(user, "superadmin") ||
    userHasRole(user, "admin") ||
    userHasRole(user, "contractor");
  const needsTenantContext =
    userHasRole(user, "contractor") || userHasRole(user, "superadmin");
  const canOpenImports =
    canManageImports && (!needsTenantContext || Boolean(activeTenantId));
  const canOpenGateways = !needsTenantContext || Boolean(activeTenantId);
  const canOpenLoRaWanDiagnostics = canEditComplex;
  const canManageIntegrations = canManageComplexes;
  const canManageMobile = canManageComplexes;
  const canManageTickets =
    userHasRole(user, "superadmin") ||
    userHasRole(user, "admin") ||
    userHasRole(user, "manager") ||
    userHasRole(user, "contractor");
  const canOpenTickets =
    canManageTickets && (!needsTenantContext || Boolean(activeTenantId));
  const isSuperadmin = userHasRole(user, "superadmin");

  useEffect(() => {
    if (!isAuthenticated || !canOpenTickets) {
      setNewTicketsCount(0);
      return undefined;
    }

    let active = true;
    const refreshNewTicketsCount = async () => {
      try {
        const result = await ticketsApi.list({ status: "new", limit: 1, offset: 0 });
        if (active) setNewTicketsCount(result.total || 0);
      } catch {
        // Keep the last known value during a temporary connection failure.
      }
    };
    const handleRefresh = () => refreshNewTicketsCount();
    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === "WEB_PUSH_RECEIVED") refreshNewTicketsCount();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshNewTicketsCount();
    };

    refreshNewTicketsCount();
    const interval = window.setInterval(refreshNewTicketsCount, 30000);
    window.addEventListener("tickets-changed", handleRefresh);
    window.addEventListener("focus", handleRefresh);
    document.addEventListener("visibilitychange", handleVisibility);
    navigator.serviceWorker?.addEventListener("message", handleServiceWorkerMessage);

    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("tickets-changed", handleRefresh);
      window.removeEventListener("focus", handleRefresh);
      document.removeEventListener("visibilitychange", handleVisibility);
      navigator.serviceWorker?.removeEventListener("message", handleServiceWorkerMessage);
    };
  }, [activeTenantId, canOpenTickets, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !canOpenGateways) {
      setGatewayProblemsCount(0);
      return undefined;
    }

    setGatewayProblemsCount(0);
    let active = true;
    const refreshGatewayProblems = async () => {
      try {
        const summary = await gatewaysApi.statusSummary();
        if (active) {
          setGatewayProblemsCount(
            Math.max(0, Number(summary?.down || 0)) +
              Math.max(0, Number(summary?.degraded || 0))
          );
        }
      } catch {
        // Keep the last known value during a temporary connection failure.
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshGatewayProblems();
    };

    refreshGatewayProblems();
    const interval = window.setInterval(refreshGatewayProblems, 30000);
    window.addEventListener("gateways-changed", refreshGatewayProblems);
    window.addEventListener("focus", refreshGatewayProblems);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("gateways-changed", refreshGatewayProblems);
      window.removeEventListener("focus", refreshGatewayProblems);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [activeTenantId, canOpenGateways, isAuthenticated]);

  const primaryItems = useMemo(
    () =>
      [
        {
          key: "home",
          label: "Главная панель",
          to: "/home",
          icon: RocketIcon,
          isVisible: true,
          isDisabled: false,
          match: (pathname) => pathname === "/" || pathname.startsWith("/home"),
        },
        {
          key: "add-complex",
          label: labels.addComplexShort,
          to: "/admin/add-complex",
          icon: PlusIcon,
          isVisible: canManageComplexes,
          isDisabled: false,
          match: (pathname) => pathname.startsWith("/admin/add-complex"),
        },
        {
          key: "meter-readings-import",
          label: "Импорт показаний",
          to: "/meter-readings/imports",
          icon: ImportIcon,
          isVisible: canManageImports,
          isDisabled: !canOpenImports,
          match: (pathname) => pathname.startsWith("/meter-readings/imports"),
        },
        {
          key: "meter-readings-progress",
          label: "Прогресс сбора",
          to: "/meter-readings/progress",
          icon: HistoryIcon,
          isVisible: canManageImports,
          isDisabled: !canOpenImports,
          match: (pathname) =>
            pathname.startsWith("/meter-readings/progress") ||
            (pathname.startsWith("/building/") && pathname.endsWith("/manual-progress")),
        },
        {
          key: "gateways",
          label: "Шлюзы и сеть",
          to: "/gateways",
          icon: AnalyticsIcon,
          isVisible: canOpenGateways,
          isDisabled: false,
          badgeCount: gatewayProblemsCount,
          badgeLabel: "Проблемных шлюзов",
          match: (pathname) => pathname.startsWith("/gateways"),
        },
        {
          key: "edge-agents",
          label: "Агенты",
          to: "/edge-agents",
          icon: AgentIcon,
          isVisible: canManageTickets,
          isDisabled: !canOpenTickets,
          match: (pathname) => pathname.startsWith("/edge-agents"),
        },
        {
          key: "lorawan-diagnostics",
          label: "Диагностика LoRaWAN",
          to: "/lorawan/diagnostics",
          icon: LoRaWanIcon,
          isVisible: canOpenLoRaWanDiagnostics,
          isDisabled: false,
          match: (pathname) => pathname.startsWith("/lorawan/diagnostics"),
        },
        {
          key: "calculations",
          label: "Расчёты",
          to: "/calculations",
          icon: CalendarIcon,
          isVisible: canManageTickets,
          isDisabled: !canOpenTickets,
          match: (pathname) => pathname.startsWith("/calculations"),
        },
        {
          key: "rte-readings",
          label: "Показания РТЭ",
          to: "/rte-readings",
          icon: SunIcon,
          isVisible: canManageTickets,
          isDisabled: !canOpenTickets,
          match: (pathname) =>
            pathname.startsWith("/rte-readings") || pathname.includes("/rte-readings"),
        },
        {
          key: "premises",
          label: labels.premises,
          to: "/premises",
          icon: KeyIcon,
          isVisible: canManageTickets && !isSettlementTenant,
          isDisabled: !canOpenTickets,
          match: (pathname) => pathname.startsWith("/premises"),
        },
        {
          key: "settlement-electricity",
          label: "Электроэнергия",
          to: "/settlement/electricity",
          icon: CalendarIcon,
          isVisible: canManageTickets && isSettlementTenant,
          isDisabled: !canOpenTickets,
          match: (pathname) => pathname.startsWith("/settlement/electricity"),
        },
        {
          key: "tickets",
          label: "Заявки",
          to: "/tickets",
          icon: TicketsIcon,
          isVisible: canManageTickets,
          isDisabled: !canOpenTickets,
          badgeCount: newTicketsCount,
          match: (pathname) => pathname.startsWith("/tickets"),
        },
        {
          key: "integrations",
          label: "API-интеграции",
          to: "/admin/integrations",
          icon: SettingsIcon,
          isVisible: canManageIntegrations,
          isDisabled: false,
          match: (pathname) => pathname.startsWith("/admin/integrations"),
        },
        {
          key: "lorawan",
          label: "BillingService LoRaWAN",
          to: "/admin/lorawan",
          icon: PhoneIcon,
          isVisible: isSuperadmin,
          isDisabled: false,
          match: (pathname) => pathname.startsWith("/admin/lorawan"),
        },
        {
          key: "mobile-registrations",
          label: "Регистрация в приложении",
          to: "/admin/mobile-registrations",
          icon: MobileUsersIcon,
          isVisible: canManageMobile,
          isDisabled: false,
          match: (pathname) => pathname.startsWith("/admin/mobile-registrations"),
        },
        {
          key: "mobile-news",
          label: "Новости мобильного приложения",
          to: "/admin/mobile-news",
          icon: MegaphoneIcon,
          isVisible: canManageMobile,
          isDisabled: false,
          match: (pathname) => pathname.startsWith("/admin/mobile-news"),
        },
      ].filter((item) => item.isVisible),
    [
      canManageComplexes,
      canManageImports,
      canManageIntegrations,
      canManageMobile,
      canManageTickets,
      canOpenGateways,
      canOpenImports,
      canOpenLoRaWanDiagnostics,
      canOpenTickets,
      isSettlementTenant,
      isSuperadmin,
      labels,
      gatewayProblemsCount,
      newTicketsCount,
    ]
  );

  const utilityItems = useMemo(
    () =>
      [
        {
          key: "add-client",
          label: "Добавить клиента",
          to: "/admin/add-client",
          icon: UserIcon,
          isVisible: isSuperadmin,
          match: (pathname) => pathname.startsWith("/admin/add-client"),
        },
        {
          key: "contractors",
          label: "Подрядчики",
          to: "/admin/contractors",
          icon: PhoneIcon,
          isVisible: isSuperadmin,
          match: (pathname) => pathname.startsWith("/admin/contractors"),
        },
        {
          key: "field-staff",
          label: "Полевые сотрудники",
          to: "/admin/field-staff",
          icon: UserIcon,
          isVisible: isSuperadmin,
          match: (pathname) => pathname.startsWith("/admin/field-staff"),
        },
      ].filter((item) => item.isVisible),
    [isSuperadmin]
  );

  if (!isAuthenticated) {
    return null;
  }

  const isVisuallyExpanded = !isCollapsed || isPeeking;

  const schedulePeekOpen = () => {
    if (!isCollapsed) return;
    if (window.matchMedia("(max-width: 900px)").matches) return;
    if (peekCloseTimerRef.current) {
      window.clearTimeout(peekCloseTimerRef.current);
      peekCloseTimerRef.current = null;
    }
    if (isPeeking) return;
    peekOpenTimerRef.current = window.setTimeout(() => {
      setIsPeeking(true);
      peekOpenTimerRef.current = null;
    }, PEEK_OPEN_DELAY_MS);
  };

  const schedulePeekClose = () => {
    if (!isCollapsed) return;
    if (peekOpenTimerRef.current) {
      window.clearTimeout(peekOpenTimerRef.current);
      peekOpenTimerRef.current = null;
    }
    peekCloseTimerRef.current = window.setTimeout(() => {
      setIsPeeking(false);
      peekCloseTimerRef.current = null;
    }, PEEK_CLOSE_DELAY_MS);
  };

  const handleTogglePinned = () => {
    clearPeekTimers();
    setIsPeeking(false);
    setIsCollapsed((prev) => !prev);
  };

  const renderNavItem = (item, index = 0) => {
    const Icon = item.icon;
    const isActive = item.match(location.pathname);
    const className = `sidebar-nav-item ${isActive ? "is-active" : ""} ${
      item.isDisabled ? "is-disabled" : ""
    }`;
    const style = { "--item-index": index };

    if (item.isDisabled) {
      return (
        <span
          key={item.key}
          className={className}
          style={style}
          title="Сначала выберите клиента"
        >
          <span className="sidebar-nav-icon">
            <Icon aria-hidden="true" />
          </span>
          <span className="sidebar-nav-label">{item.label}</span>
        </span>
      );
    }

    return (
      <NavLink
        key={item.key}
        to={item.to}
        className={className}
        style={style}
        title={item.label}
      >
        <span className="sidebar-nav-icon">
          <Icon aria-hidden="true" />
        </span>
        <span className="sidebar-nav-label">{item.label}</span>
        {item.badgeCount > 0 ? (
          <span
            className="sidebar-nav-badge"
            aria-label={`${item.badgeLabel || "Проблем"}: ${item.badgeCount}`}
          >
            {item.badgeCount > 99 ? "99+" : item.badgeCount}
          </span>
        ) : null}
      </NavLink>
    );
  };

  return (
    <div
      className={`sidebar-rail ${isCollapsed ? "is-collapsed" : ""} ${
        isPeeking ? "is-peeking" : ""
      }`}
      style={{
        "--sidebar-rail-width": isCollapsed
          ? SIDEBAR_COLLAPSED_WIDTH
          : SIDEBAR_EXPANDED_WIDTH,
      }}
    >
      <aside
        className={`sidebar ${isCollapsed ? "is-collapsed" : ""} ${
          isPeeking ? "is-peeking" : ""
        } ${isVisuallyExpanded ? "is-expanded" : ""}`}
        onMouseEnter={schedulePeekOpen}
        onMouseLeave={schedulePeekClose}
        onFocusCapture={schedulePeekOpen}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            schedulePeekClose();
          }
        }}
      >
        <div className="sidebar-top">
          <Link to="/home" className="sidebar-brand" aria-label="Главная">
            <img src={logo} alt="" className="sidebar-logo" />
            {isVisuallyExpanded ? (
              <span className="sidebar-brand-label">Billing Service</span>
            ) : null}
          </Link>

          <button
            className="sidebar-toggle"
            type="button"
            onClick={handleTogglePinned}
            aria-label={isCollapsed ? "Развернуть меню" : "Свернуть меню"}
            title={
              isCollapsed
                ? isPeeking
                  ? "Закрепить развёрнутым"
                  : "Развернуть меню"
                : "Свернуть меню"
            }
          >
            <span className="sidebar-toggle-icon-wrap" aria-hidden="true">
              <ChevronRightRounded className="sidebar-toggle-icon" />
            </span>
            <span className="sidebar-toggle-label">
              {isCollapsed ? "Закрепить" : "Свернуть"}
            </span>
          </button>
        </div>

        <div
          className={`sidebar-nav-shell ${canScrollNav ? "can-scroll" : ""} ${
            navScrollEdges.top ? "has-top-fade" : ""
          } ${navScrollEdges.bottom ? "has-bottom-fade" : ""}`}
        >
          <nav
            ref={navRef}
            className="sidebar-nav"
            aria-label="Основная навигация"
          >
            {primaryItems.map((item, index) => renderNavItem(item, index))}
          </nav>
        </div>

        <div className="sidebar-bottom">
          {utilityItems.length > 0 && (
            <div className="sidebar-utilities">
              {utilityItems.map((item, index) =>
                renderNavItem(item, primaryItems.length + index)
              )}
            </div>
          )}

          <button
            className="sidebar-nav-item sidebar-theme-button"
            type="button"
            onClick={(event) => {
              const nextTheme = themeId === "light" ? "dark" : "light";
              const root = document.documentElement;
              const rect = event.currentTarget.getBoundingClientRect();
              root.style.setProperty("--theme-x", `${rect.left + rect.width / 2}px`);
              root.style.setProperty("--theme-y", `${rect.top + rect.height / 2}px`);

              if (typeof document.startViewTransition === "function") {
                document.startViewTransition(() => setThemeId(nextTheme));
              } else {
                setThemeId(nextTheme);
              }
            }}
            title={`Тема: ${themeId === "light" ? "светлая" : "тёмная"}`}
          >
            <span className="sidebar-nav-icon">
              <SunIcon aria-hidden="true" />
            </span>
            <span className="sidebar-nav-label">
              {themeId === "light" ? "Светлая тема" : "Тёмная тема"}
            </span>
          </button>
        </div>
      </aside>
    </div>
  );
};

export default Sidebar;
